import { useCallback, useState } from "react";

import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import useFeatureFlags from "@/hooks/useFeatureFlags";
import { isUserInTreatment } from "@/hooks/usePatientStatus";
import useScheduleQueryPatientId from "@/hooks/useScheduleQueryPatientId";
import { useGetScheduleEventsQuery } from "@/store/api/schedule/schedule";
import type { AppUserSliceState } from "@/store/reducers/appUser";
import {
  useAppUserLinkedPatientsSelector,
  useAppUserProxyPatientsInTreatmentSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";

enum UseScheduleEventsOptions {
  defaultPageSize = 30,
}

interface UseScheduleEventsProps {
  isPastEvents: boolean;
  isPatient?: boolean;
  pageSize?: number;
  patientExternalId: string;
}

interface UseScheduleEvents {
  isLoading: boolean;
  pageNumber: number;
  refetch: () => Promise<void> | void;
  resetAndLoadListData: () => void;
  scheduleEvents: GroupedCombinedScheduleDataListType[];
  scheduleEventsError: FetchBaseQueryError | SerializedError | undefined;
}

interface ResolveQueryPermissionsResult {
  shouldSkipGroupClassQuery: boolean;
}

const canQueryPatient = (patient?: LinkedPatientProfile): boolean =>
  Boolean(
    patient?.universalStatusCode &&
    isUserInTreatment(patient.universalStatusCode),
  );

const resolveQueryPermissions = ({
  appUser,
  linkedPatients,
  patientIdArg,
  proxyPatientsInTreatment,
}: {
  appUser: AppUserSliceState;
  linkedPatients: LinkedPatientProfile[];
  patientIdArg?: string;
  proxyPatientsInTreatment: LinkedPatientProfile[];
}): ResolveQueryPermissionsResult => {
  if (appUser.isPatient) {
    return {
      shouldSkipGroupClassQuery: !appUser.isInTreatment,
    };
  }

  if (patientIdArg) {
    const selectedPatient = proxyPatientsInTreatment.find(
      (p) => p.externalId === patientIdArg,
    );
    return {
      shouldSkipGroupClassQuery: !canQueryPatient(selectedPatient),
    };
  }

  const hasLinkedPatientInTreatment = linkedPatients.some(
    (p) => p.universalStatusCode && isUserInTreatment(p.universalStatusCode),
  );
  return {
    shouldSkipGroupClassQuery: !hasLinkedPatientInTreatment,
  };
};
const useScheduleEvents = ({
  isPastEvents,
  isPatient,
  pageSize = UseScheduleEventsOptions.defaultPageSize,
  patientExternalId: patientIdArg,
}: UseScheduleEventsProps): UseScheduleEvents => {
  const patientExternalId = useScheduleQueryPatientId(patientIdArg);

  const appUser = useAppUserSelector();
  const linkedPatients = useAppUserLinkedPatientsSelector() ?? [];
  const proxyPatientsInTreatment =
    useAppUserProxyPatientsInTreatmentSelector() ?? [];

  const { shouldSkipGroupClassQuery } = resolveQueryPermissions({
    appUser,
    linkedPatients,
    patientIdArg: patientExternalId,
    proxyPatientsInTreatment,
  });

  const { isGroupClassRedesignEnabled } = useFeatureFlags();

  const [pageNumber, setPageNumber] = useState(1);

  const {
    data: scheduleEvents,
    error: scheduleEventsError,
    isFetching: isScheduleFetching,
    isLoading: isScheduleLoading,
    refetch: refetchScheduleEvents,
  } = useGetScheduleEventsQuery({
    isPastEvents,
    isPatient,
    isV2GroupsEnabled: isGroupClassRedesignEnabled,
    pageNumber,
    pageSize,
    patientExternalId,
    shouldSkipGroupClassQuery,
  });

  const resetAndLoadListData = useCallback(() => {
    setPageNumber(1);
    void refetchScheduleEvents();
  }, [refetchScheduleEvents]);

  return {
    isLoading: isScheduleLoading || isScheduleFetching,
    pageNumber,
    refetch: async () => {
      await refetchScheduleEvents();
    },
    resetAndLoadListData,
    scheduleEvents: scheduleEvents || [],
    scheduleEventsError,
  };
};

export { canQueryPatient, resolveQueryPermissions };
export type { UseScheduleEventsProps };
export default useScheduleEvents;
